import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import AuthLayout from "@/components/layouts/AuthLayout";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

const MagicLinkCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    if (hasProcessed) return;
    setHasProcessed(true);

    const handleMagicLinkAuth = async () => {
      try {
        console.log("MagicLinkCallback: Processing magic link authentication");
        console.log("Current URL:", window.location.href);
        console.log("Hash:", location.hash);
        console.log("Search:", location.search);

        // Check for token in URL search params (direct Supabase link format)
        const searchParams = new URLSearchParams(location.search);
        const urlToken = searchParams.get("token");
        const tokenType = searchParams.get("type");

        if (urlToken) {
          console.log("Found token in search params, type:", tokenType);

          // Verify the OTP token
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: urlToken,
            type: tokenType === "magiclink" ? "magiclink" : "email",
          });

          if (verifyError) {
            console.error("Magic link verification failed:", verifyError);
            throw new Error(
              verifyError.message || "Invalid or expired magic link"
            );
          }

          if (data.session) {
            console.log("Magic link verified, session established");
            toast({
              title: "Welcome!",
              description: "You have been logged in successfully.",
            });
            navigate("/backoffice/dashboard", { replace: true });
            return;
          }

          throw new Error("No session returned from verification");
        }

        // Check for tokens in hash (client-side redirect format)
        const hash = location.hash;
        if (hash && hash.includes("access_token=")) {
          console.log("Found access_token in hash");

          const accessTokenMatch = hash.match(/access_token=([^&]*)/);
          const refreshTokenMatch = hash.match(/refresh_token=([^&]*)/);
          const typeMatch = hash.match(/type=([^&]*)/);

          console.log("Token type from hash:", typeMatch?.[1]);

          if (accessTokenMatch?.[1]) {
            const accessToken = accessTokenMatch[1];
            const refreshToken = refreshTokenMatch?.[1] || "";

            // Set the session with the tokens
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (sessionError) {
              console.error("Error setting session:", sessionError);
              throw new Error(
                sessionError.message || "Failed to establish session"
              );
            }

            // Verify the session was established
            const { data: userData, error: userError } =
              await supabase.auth.getUser();

            if (userError || !userData.user) {
              console.error("Error getting user after session set:", userError);
              throw new Error("Failed to verify authentication");
            }

            console.log("Session established for user:", userData.user.email);

            toast({
              title: "Welcome!",
              description: "You have been logged in successfully.",
            });

            navigate("/backoffice/dashboard", { replace: true });
            return;
          }
        }

        // No valid tokens found
        throw new Error(
          "No authentication token found. The magic link may be invalid or expired."
        );
      } catch (err: any) {
        console.error("Magic link authentication error:", err);
        setError(err.message || "Authentication failed");

        toast({
          title: "Authentication Failed",
          description:
            err.message || "Please request a new magic link and try again.",
          variant: "destructive",
        });
      }
    };

    handleMagicLinkAuth();
  }, [location, navigate, toast, hasProcessed]);

  if (error) {
    return (
      <AuthLayout title="Authentication Error" subtitle="Unable to complete login">
        <div className="flex flex-col items-center justify-center p-8 space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => navigate("/auth/login")}>
              Return to Login
            </Button>
            <Button onClick={() => navigate("/auth/forgot-password")}>
              Request New Link
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Signing In" subtitle="Please wait...">
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        <p className="text-sm text-center text-muted-foreground">
          Verifying your magic link...
        </p>
      </div>
    </AuthLayout>
  );
};

export default MagicLinkCallback;
